import { DateTime } from 'luxon';
import { formatClock } from '@tradeoutlet/shared';
import { DATE_FORMATS } from './format';
import { getClockPref } from './clockPref';

/**
 * Review Low (native date/time inputs): the browser draws <input type=date|time> in the OS/browser locale, so a
 * US-locale tablet shows 01/02/2026 and 2:05 PM. When the locale doesn't already read the UK way, we show the
 * value in house format next to the field. UK browsers see no change.
 */
export function localeOrder(locale?: string) {
  const parts = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).formatToParts(new Date(2026, 0, 2));
  const types = parts.filter(p => p.type === 'day' || p.type === 'month' || p.type === 'year').map(p => p.type);
  const hour12 = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hour12 ?? false;
  return { dayFirst: types[0] === 'day', hour12 };
}

let cached: ReturnType<typeof localeOrder> | null = null;
const current = () => (cached ??= localeOrder());
/** Test hook. */
export const _resetLocaleCache = () => { cached = null; };

export function inputHint(type: string | undefined, value: unknown, order = current()): string | null {
  if (typeof value !== 'string' || !value) return null;
  if (type === 'date') {
    if (order.dayFirst) return null;
    const d = DateTime.fromISO(value);
    return d.isValid ? d.toFormat(`ccc ${DATE_FORMATS.date}`) : null;
  }
  if (type === 'time') {
    // Only when the browser draws the clock differently from the person's 12 / 24-hour preference.
    const pref = getClockPref();
    if (order.hour12 === (pref === '12h')) return null;
    const t = DateTime.fromISO(`2000-01-01T${value}`);
    return t.isValid ? formatClock(t, pref) : null;
  }
  if (type === 'datetime-local') {
    if (order.dayFirst && !order.hour12) return null;
    const d = DateTime.fromISO(value);
    return d.isValid ? d.toFormat(DATE_FORMATS.weekdayTime) : null;
  }
  return null;
}
